import { FACT_ATTRIBUTE_IDS, FACT_IDS } from '../../components/Shared/Factsheet/constants'
import { getFactsheetBlueprint } from '../../api'

// de-dupes concurrent blueprint fetches triggered by ensureBlueprint().
let blueprintFetchInFlight = null

/**
 * Flattens the raw factsheet-blueprint API response into the shape stored in
 * redux: `factsheet`.`components` + `factTree`.
 *
 * @param {Object} res the getFactsheetBlueprint() response ({ factsheet, factTree })
 * @returns {Object} { ...res.factsheet, factTree }
 */
export const normalizeBlueprint = (res) => ({
  ...res.factsheet,
  factTree: res.factTree,
})

const putFactsheet = (factsheet) => ({
  type: 'FACTSHEET_PUT',
  payload: {
    factsheet
  }
})

const addFactInstance = (factDefId, data, meta) => ({
  type: 'FACTSHEET_ADD_FACT_INSTANCE',
  payload: {
    factDefId,
    data
  },
  // `meta.skipContradictionCheck` marks a programmatic set so the
  // factsheet-contradiction middleware lets it through unchecked.
  ...(meta ? { meta } : {})
})

const putFactInstance = (factDefId, factInstanceKey, data) => ({
  type: 'FACTSHEET_PUT_FACT_INSTANCE',
  payload: {
    factDefId,
    factInstanceKey,
    data
  }
})

const deleteFactInstance = (factDefId, factInstanceKey) => ({
  type: 'FACTSHEET_DELETE_FACT_INSTANCE',
  payload: {
    factDefId,
    factInstanceKey
  }
})

const deleteFact = (factDefId) => ({
  type: 'FACTSHEET_DELETE_FACT',
  payload: {
    factDefId
  }
})

const putAttribute = (factDefId, factInstanceKey, attributeDefId, data, meta, removeAttributeDefIds = []) => ({
  type: 'FACTSHEET_PUT_ATTRIBUTE',
  payload: {
    factDefId,
    factInstanceKey,
    attributeDefId,
    data,
    // other attribute ids to drop on the same instance as part of this set, so a
    // "replace attribute A with attribute B" stays one atomic, interceptable action
    // (see the factsheet-contradiction middleware: a held set must apply or undo as a unit).
    removeAttributeDefIds,
  },
  // `meta.skipContradictionCheck` marks a programmatic set so the
  // factsheet-contradiction middleware lets it through unchecked.
  ...(meta ? { meta } : {})
})

const deleteAttribute = (factDefId, factInstanceKey, attributeDefId) => ({
  type: 'FACTSHEET_DELETE_ATTRIBUTE',
  payload: {
    factDefId,
    factInstanceKey,
    attributeDefId
  }
})

const putValidationStatus = (factDefId, factInstanceKey, attributeDefId, isValid) => ({
  type: 'FACTSHEET_PUT_VALIDATION_STATUS',
  payload: {
    factDefId,
    factInstanceKey,
    attributeDefId,
    isValid
  }
})

const putFactInstanceExpandedStatus = (factDefId, factInstanceKey, expanded) => ({
  type: 'FACTSHEET_PUT_FACT_INSTANCE_EXPANDED_STATUS',
  payload: {
    factDefId,
    factInstanceKey,
    expanded
  }
})

const putCountryCodeFact = (countryCode) => {
  return (dispatch) => {
    dispatch(putAttribute(
      String(FACT_IDS.COUNTRY),
      0,
      String(FACT_ATTRIBUTE_IDS.COUNTRY_CODE),
      {
        attributeDefId: String(FACT_ATTRIBUTE_IDS.COUNTRY_CODE),
        value: countryCode,
      },
      { skipContradictionCheck: true }
    ))
  }
}

const putBlueprint = (blueprint) => ({
  type: 'FACTSHEET_PUT_BLUEPRINT',
  payload: {
    blueprint
  }
})

/**
 * Requests that the factsheet be scrolled into view once rendered, without
 * running a search — used by the notification center's "review factsheet"
 * button so the customer lands on the factsheet section.
 */
const setPendingFactsheetScroll = () => ({
  type: 'FACTSHEET_SET_PENDING_FACTSHEET_SCROLL',
})

const clearPendingFactsheetScroll = () => ({
  type: 'FACTSHEET_CLEAR_PENDING_FACTSHEET_SCROLL',
})

/**
 * Stores a fact def-id to jump to once the factsheet is rendered: the
 * section-group-container selects the fact's group, scrolls to it and briefly
 * highlights it. Used to take the user straight to a contradicting fact clicked
 * in the notification center.
 */
const setPendingFactFocus = (factId) => ({
  type: 'FACTSHEET_SET_PENDING_FACT_FOCUS',
  payload: {
    factId
  }
})

const clearPendingFactFocus = () => ({
  type: 'FACTSHEET_CLEAR_PENDING_FACT_FOCUS',
})

/**
 * Loads the (global, property-independent) factsheet blueprint into redux, but
 * only if it isn't already present — so it is fetched once per session and
 * reused across properties and views. This is the single loader for the
 * blueprint; both the notification detail view (which just needs labels) and
 * EditProperty (which clones it for the editable factsheet) go through here.
 * Concurrent calls share a single in-flight request.
 *
 * Always resolves — with the stored blueprint on success, or `null` on failure
 * (never rejects) — so fire-and-forget callers can't trigger an unhandled
 * rejection while callers that need it can branch on the result.
 *
 * @returns {Promise<Object|null>} the redux blueprint, or null if it failed to load
 */
const ensureBlueprint = () => (dispatch, getState) => {
  if (getState().factsheet.blueprint) {
    return Promise.resolve(getState().factsheet.blueprint)
  }
  if (blueprintFetchInFlight) {
    return blueprintFetchInFlight
  }
  const { fetchPromise } = getFactsheetBlueprint()
  blueprintFetchInFlight = fetchPromise
    .then((response) => {
      dispatch(putBlueprint(normalizeBlueprint(response)))
      return getState().factsheet.blueprint
    })
    .catch((error) => {
      console.error('Failed to load factsheet blueprint, ' + error)
      return null
    })
    .finally(() => {
      blueprintFetchInFlight = null
    })
  return blueprintFetchInFlight
}

export const factsheetActions = {
  putFactsheet,
  addFactInstance,
  putFactInstance,
  deleteFactInstance,
  deleteFact,
  putAttribute,
  deleteAttribute,
  putValidationStatus,
  putFactInstanceExpandedStatus,
  putCountryCodeFact,
  putBlueprint,
  ensureBlueprint,
  setPendingFactsheetScroll,
  clearPendingFactsheetScroll,
  setPendingFactFocus,
  clearPendingFactFocus,
}
